import { t } from "@lingui/macro";
import { CommissionUnit } from "@src/__generated__/urql-graphql";
import type { AppStore } from "@src/stores/AppStore";
import { required } from "@src/utils/forms/validators";
import { NumberModel } from "@src/utils/models";
import { isBefore } from "date-fns";
import { FieldState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";
import { ExpenseCategory, OurWorkCategory } from "../models";
import type {
  TCategory,
  TExpenseBudgetCategories,
  TOurWorkBudgetCategories,
  TProject,
} from "../types";

export class Store {
  appStore: AppStore;

  /**
   * All categories without budgetItems.
   * Project itself doesn't return all categories.
   */
  private _ourWorkCategories: TOurWorkBudgetCategories = [];
  private _expenseCategories: TExpenseBudgetCategories = [];

  @observable.ref project?: TProject;
  @observable.ref ourWorkCategories: OurWorkCategory[] = [];
  @observable.ref expenseCategories: ExpenseCategory[] = [];
  @observable projectLoading = false;
  refetch?: () => any;

  @observable.ref selectedBudgetCategory: TCategory;
  @observable.ref defaultDate?: Date;
  @observable.ref lowestPossibleDate?: Date;
  valid_from: FieldState<Date | undefined>;

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
    this.dateGteLowestPossibleDate = this.dateGteLowestPossibleDate.bind(this);
    this.valid_from = new FieldState<Date | undefined>(
      this.lowestPossibleDate,
    ).validators(required, this.dateGteLowestPossibleDate);
  }

  @action setDefaultDate = () => {
    this.valid_from.onChange(this.defaultDate);
  };

  @computed get filteredCategories() {
    if (!this.selectedBudgetCategory) {
      return [...this.ourWorkCategories, ...this.expenseCategories];
    }

    if (this.selectedBudgetCategory.type === "ourWork") {
      const category = this.ourWorkCategories.find(
        (i) => i.budget_category_id === this.selectedBudgetCategory?.id,
      );

      if (category) {
        return [category];
      }

      return [];
    }

    if (this.selectedBudgetCategory.type === "expense") {
      const category = this.expenseCategories.find(
        (i) => i.budget_category_id === this.selectedBudgetCategory?.id,
      );

      if (category) {
        return [category];
      }

      return [];
    }

    return [];
  }

  setCategoriesWithoutBudgetItems(src: {
    ourWorkCategories: TOurWorkBudgetCategories;
    expenseCategories: TExpenseBudgetCategories;
  }) {
    this._ourWorkCategories = src.ourWorkCategories;
    this._expenseCategories = src.expenseCategories;
  }

  @action
  setProject(project: TProject) {
    this.project = project;

    const { denomination } = this.appStore.workspaceStore.settings!.currency;

    this.ourWorkCategories = this._ourWorkCategories.map((i) => {
      const category = this.project?.ourWorkStructured.find(
        (j) => j.budget_category_id === i.id,
      );

      if (category) {
        const ourWorkCategory = new OurWorkCategory(category);

        ourWorkCategory.initBudgets(
          category.budgetItems.map((k) => ({
            id: k.id,
            title: k.title,
            our_work_budget_category_id: i.id,
            total_budget: new NumberModel(k.stats.total_budget, denomination),
            credit: k.stats.credit,
          })),
        );

        return ourWorkCategory;
      }

      return new OurWorkCategory({ budget_category_id: i.id, name: i.name });
    });

    this.expenseCategories = (this._expenseCategories || []).map((i) => {
      const category = this.project?.expensesWithoutOtherStructured.find(
        (j) => j.budget_category_id === i.id,
      );

      if (category) {
        const expenseCategory = new ExpenseCategory(category);

        expenseCategory.initBudgets(
          category.budgetItems.map((k) => ({
            expense_budget_category_id: i.id,
            id: k.id,
            title: k.title,
            credit: k.stats.credit,
            completed_at: k.completed_at,
            real_expenses: k.stats.real_expenses,
            commission_unit: CommissionUnit.Absolute,
            total_budget: new NumberModel(k.stats.total_budget, denomination),
            planned_expenses: new NumberModel(
              k.stats.planned_expenses,
              denomination,
            ),
            planned_commission: new NumberModel(
              k.stats.planned_commission,
              denomination,
            ),
          })),
        );

        return expenseCategory;
      }

      return new ExpenseCategory({ budget_category_id: i.id, name: i.name });
    });
  }

  @action
  setProjectLoading(value: boolean) {
    this.projectLoading = value;
  }

  @action.bound
  selectBudgetCategory(category: TCategory) {
    this.selectedBudgetCategory = category;
  }

  dateGteLowestPossibleDate($: Date | undefined) {
    if (!this.lowestPossibleDate || !$) return null;

    $.setHours(0, 0, 0, 0);
    this.lowestPossibleDate.setHours(0, 0, 0, 0);

    if (isBefore($, this.lowestPossibleDate)) {
      return t`Cannot be assigned to a closed month`;
    }

    return null;
  }
}
